import { Flex, message, Modal, Spin, Tabs } from 'antd';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import { getCustomers } from '~/services/hooks/customers';
import { createEvents, updateEvents } from '~/services/hooks/events';
import { getUsers } from '~/services/hooks/users';
import errorHandler from '~/Utils/errorHandler';
import * as Yup from 'yup';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { EventModal } from './event';
import { OperationalModal } from './operationals';
import ServiceModal from './services';
import ExpenseModal from './expenses';
import DescriptionModal from './descriptions';
import TeamModal from './team';
import { ConsignedsModal } from './consigneds';
import { FaExclamationCircle, FaHistory } from 'react-icons/fa';
import Button from '~/components/Button';
import ActivityLogsModal from './modals/activityLogs';

const initial = {
  name: '',
  starts_at: '',
  ends_at: '',
  adress_street: '',
  adress_number: '',
  adress_cep: '',
  adress_reference: '',
  adress_local_name: '',
  adress_complement: '',
  estimated_people: '',
  status: null,
  note_internal: '',
  note_public: '',
  description: '',
  city_id: null,
  sectors: [],
  services_events: [],
  event_expenses: [],
  team: [],
  consigneds: [],
  protocol: '',
  note_consigned: '',
  price_discount: '',
  created_by_id: null,
  updated_by_id: null,
  customer_id: null,
  note_after_event: '',
  seller_id: null,
  payment_status: 0,
  stock_write_offs: null,
};

const tabFieldGroups = {
  Event: [
    'name',
    'starts_at',
    'ends_at',
    'adress_cep',
    'estimated_people',
    'status',
    'city_id',
    'customer_id',
    'seller_id',
    'payment_status',
  ],
  operational: ['sectors'],
  services: ['services_events'],
  expenses: ['event_expenses'],
  team: ['team'],
  consigneds: ['consigneds'],
};

const checkTabErrors = (errors, tabFields, excludeKey = null, includeNestedKey = null) => {
  let hasNormalError = tabFields?.some((field) => {
    const errorData = errors[field];
    if (!errorData) return false;

    if (excludeKey) {
      const hasOtherErrors = (obj) => {
        if (typeof obj !== 'object' || obj === null) return true;
        return Object.keys(obj).some((k) => {
          if (k === excludeKey) return false;
          return hasOtherErrors(obj[k]);
        });
      };
      return hasOtherErrors(errorData);
    }
    return true;
  });

  if (includeNestedKey && !hasNormalError) {
    const hasNested = (obj) => {
      if (typeof obj !== 'object' || obj === null) return false;
      return Object.keys(obj).some((k) => {
        if (k === includeNestedKey) return true;
        return hasNested(obj[k]);
      });
    };
    if (hasNested(errors)) {
      hasNormalError = true;
    }
  }

  return hasNormalError;
};

export default function FormEvents({ visible, event, onClose, isMobile, eventRevenues = false }) {
  const { t } = useTranslation();
  const authCheck = useCheckAuth();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(initial);
  const [customers, setCustomers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [logsVisible, setLogsVisible] = useState(false);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      if (event) {
        const formatted = { ...event };

        formatted?.sectors?.forEach((s) => {
          s?.sector_operationals?.forEach((so) => {
            if (so?.invite_price == null || so?.invite_price === '') {
              so.invite_price = so?.price_unit;
            }
          });
        });

        setInitialValues(formatted);
      } else {
        setInitialValues(initial);
      }
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const dataCustomers = await getCustomers('', 'no-paginated');
      const dataSellers = await getUsers('', 'no-paginated');

      setCustomers(dataCustomers);
      setSellers(dataSellers);
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const saveEvent = async (values, { setErrors }) => {
    setLoading(true);
    try {
      if (event) {
        await updateEvents(event.id, values);
      } else {
        await createEvents(values);
      }
      message.success('Evento salvo com sucesso!');
      onClose();
    } catch (error) {
      console.error(error);
      setErrors(errorHandler(error));
    }
    setLoading(false);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Nome é obrigatório'),
    starts_at: Yup.date().required('Data de início é obrigatória'),
    ends_at: Yup.date()
      .required('Data de término é obrigatória')
      .min(Yup.ref('starts_at'), 'Data de término deve ser menor ou igual à data de início'),
    estimated_people: Yup.number()
      .nullable()
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .positive('Deve ser um número positivo'),
    status: Yup.string().required('Status é obrigatório'),
    city_id: Yup.number().nullable(),
    sectors: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Nome do setor é obrigatório'),
        sector_operationals: Yup.array()
          .of(
            Yup.object().shape({
              operational_id: Yup.number()
                .required('O Operacional é obrigatório')
                .typeError('O Operacional deve ser um número'),
              amount: Yup.number()
                .nullable()
                .required('Quantidade é obrigatória')
                .positive('Quantidade deve ser um número positivo'),
              price_unit: Yup.string().required('Preço unitário é obrigatório'),
              invite_price: Yup.string().required('Preço (convite) é obrigatório'),
              price_total: Yup.string().required('Preço total é obrigatório'),
            })
          )
          .nullable(),
      })
    ),
    services_events: Yup.array()
      .of(
        Yup.object().shape({
          service: Yup.object()
            .shape({
              id: Yup.number().required('O serviço é obrigatório').typeError('O ID do serviço deve ser um número'),
            })
            .required('O serviço é obrigatório')
            .typeError('O serviço deve ser um objeto'),
          amount: Yup.number().required('Quantidade é obrigatória').positive('Quantidade deve ser um número positivo'),
          price_unit: Yup.string().required('Preço unitário é obrigatório'),
        })
      )
      .nullable(),
    event_expenses: Yup.array()
      .of(
        Yup.object().shape({
          expense: Yup.object()
            .shape({
              id: Yup.number().required('A despesa é obrigatória').typeError('O ID da despesa deve ser um número'),
            })
            .required('A despesa é obrigatória')
            .typeError('A despesa deve ser um objeto'),
          amount: Yup.number().required('Quantidade é obrigatória').positive('Quantidade deve ser um número positivo'),
          price_unit: Yup.string().required('Preço unitário é obrigatório'),
        })
      )
      .nullable(),
    team: Yup.array().of(
      Yup.object().shape({
        departure_at: Yup.string().required('Hora de saída é obrigatória'),
      })
    ),
    customer_id: Yup.number().nullable(),
    seller_id: Yup.number().required('Vendedor é obrigatório'),
    payment_status: Yup.string().required('Status de pagamento é obrigatório'),
    consigneds: Yup.array().of(
      Yup.object().shape({
        item_id: Yup.number().required('Produto é obrigatório'),
        amount: Yup.number().nullable(),
        weight_total: Yup.number().nullable(),
        price_unit: Yup.string().nullable(),
        price_total: Yup.string().nullable(),
      })
    ),
  });

  useEffect(() => {
    if (visible) {
      fetchEvent();
      fetchAll();
    }
  }, [visible, event]);

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={saveEvent}
    >
      {({ errors, isSubmitting, submitForm, resetForm, setFieldValue, values }) => {
        if (Object.keys(errors).length > 0) {
          console.error('ERROS DO YUP:', errors);
        }
        return (
          <Modal
            title={
              event
                ? t('screens:events.modal.editTitle') + ' - ' + values.name
                : t('screens:events.modal.newTitle') + ' - ' + values.name
            }
            onCancel={onClose}
            style={{ maxWidth: isMobile ? '100%' : '1700px' }}
            afterClose={() => {
              resetForm();
              setInitialValues(initial);
              setCustomers([]);
              setSellers([]);
            }}
            open={visible}
            centered
            width={isMobile ? '100%' : '1200px'}
            footer={
              <Flex align="center" justify="space-between">
                <Button color="secundary" onClick={() => setLogsVisible(true)}>
                  <FaHistory /> {t('Histórico de modificações')}
                </Button>
                <ModalFooter onOk={submitForm} loading={loading || isSubmitting} onCancel={onClose} />
              </Flex>
            }
          >
            <Spin spinning={loading}>
              <Tabs
                defaultActiveKey="1"
                size="large"
                // style={{
                //   marginBottom: 32,
                // }}
                items={[
                  {
                    label: (
                      <>
                        Geral{' '}
                        {checkTabErrors(errors, tabFieldGroups.Event) && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'Event',
                    forceRender: true,
                    children: (
                      <EventModal
                        eventRevenues={eventRevenues}
                        t={t}
                        event={event}
                        customers={customers}
                        values={values}
                        setFieldValue={setFieldValue}
                        sellers={sellers}
                        errors={errors}
                        isMobile={isMobile}
                        loading={loading}
                        setLoading={setLoading}
                      />
                    ),
                  },
                  authCheck('@events/operationalView') && {
                    label: (
                      <>
                        Operacional/Setores{' '}
                        {checkTabErrors(errors, tabFieldGroups.operational, 'invite_price') && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'operational',
                    forceRender: true,
                    children: (
                      <OperationalModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                      />
                    ),
                  },
                  authCheck('@events/servicesView') && {
                    label: (
                      <>
                        Serviços{' '}
                        {checkTabErrors(errors, tabFieldGroups.services) && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'services',
                    forceRender: true,
                    children: (
                      <ServiceModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                      />
                    ),
                  },
                  authCheck('@events/expensesView') && {
                    label: (
                      <>
                        Outras despesas{' '}
                        {checkTabErrors(errors, tabFieldGroups.expenses) && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'expenses',
                    forceRender: true,
                    children: (
                      <ExpenseModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                      />
                    ),
                  },
                  authCheck('@events/obsView') && {
                    label: 'Observações',
                    key: 'descriptions',
                    children: (
                      <DescriptionModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                      />
                    ),
                  },
                  authCheck('@events/teamView') && {
                    label: (
                      <>
                        Equipe{' '}
                        {checkTabErrors(errors, tabFieldGroups.team, null, 'invite_price') && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'team',
                    forceRender: true,
                    children: (
                      <TeamModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                        isMobile={isMobile}
                      />
                    ),
                  },
                  authCheck('@events/itemsView') && {
                    label: (
                      <>
                        Bebidas{' '}
                        {checkTabErrors(errors, tabFieldGroups.consigneds) && (
                          <span style={{ color: 'red' }}>
                            <FaExclamationCircle />
                          </span>
                        )}
                      </>
                    ),
                    key: 'itens',
                    forceRender: true,
                    children: (
                      <ConsignedsModal
                        eventRevenues={eventRevenues}
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        setLoading={setLoading}
                        loading={loading}
                        visible={visible}
                        errors={errors}
                        isMobile={isMobile}
                      />
                    ),
                  },
                ]}
              />
            </Spin>
            <ActivityLogsModal
              visible={logsVisible}
              eventId={event?.id}
              eventName={event?.name}
              onClose={() => setLogsVisible(false)}
            />
          </Modal>
        );
      }}
    </Formik>
  );
}
